import type { Achievement, Assignment, Game, Unit, User, UserAchievement } from '@/types';

export const DEMO_EMAIL = 'demo@ludolio.com';

const iso = (daysAgo: number) => new Date(Date.now() - daysAgo * 86400000).toISOString();
const makeId = (prefix: string, n: number) => `${prefix}-${String(n).padStart(4, '0')}`;

const demoTeacher: User = { id: 'demo-teacher-0000', email: DEMO_EMAIL, firstName: 'Demo', lastName: 'Teacher', orgUsername: 'demo.teacher', role: 'teacher', createdAt: iso(120) };
const teacherPool: User[] = [
  demoTeacher,
  { id: makeId('teacher', 1), email: 'casey.morgan@demo-staff.ludolio.com', firstName: 'Casey', lastName: 'Morgan', role: 'teacher', createdAt: iso(90) },
  { id: makeId('teacher', 2), email: 'jordan.lee@demo-staff.ludolio.com', firstName: 'Jordan', lastName: 'Lee', role: 'teacher', createdAt: iso(75) },
  { id: makeId('teacher', 3), email: 'avery.patel@demo-staff.ludolio.com', firstName: 'Avery', lastName: 'Patel', role: 'teacher', createdAt: iso(60) },
];

const games: Game[] = [
  { id: makeId('game', 1), title: 'Mechanic Forge', description: 'A systems design sandbox where students tune core loops, reward cadence, and difficulty pacing.', publisher: 'Ludolio Studio', version: '1.4.2', status: 'published', executablePath: 'MechanicForge.exe', isPublished: true, createdAt: iso(180), updatedAt: iso(7) },
  { id: makeId('game', 2), title: 'Level Lab', description: 'A level design game focused on pathing, encounter flow, sightlines, and tutorial spaces.', publisher: 'Ludolio Studio', version: '0.9.7', status: 'published', executablePath: 'LevelLab.exe', isPublished: true, createdAt: iso(160), updatedAt: iso(10) },
  { id: makeId('game', 3), title: 'Narrative Nexus', description: 'A narrative design experience for branching dialogue, quest structure, and player choice.', publisher: 'Ludolio Studio', version: '2.0.1', status: 'published', executablePath: 'NarrativeNexus.exe', isPublished: true, createdAt: iso(145), updatedAt: iso(5) },
  { id: makeId('game', 4), title: 'Playtest Studio', description: 'A draft playtesting toolkit for tutorial UX, feedback loops, and retention friction analysis.', publisher: 'Partner Press', version: '1.1.0', status: 'draft', executablePath: 'PlaytestStudio.exe', isPublished: false, createdAt: iso(110), updatedAt: iso(22) },
];

const firstNames = ['Alex', 'Blake', 'Chris', 'Dana', 'Elliot', 'Fin', 'Gale', 'Harper', 'Indie', 'Jamie', 'Kai', 'Logan', 'Morgan', 'Noor', 'Owen', 'Priya', 'Quinn', 'Riley', 'Sam', 'Tara', 'Uma', 'Vince', 'Wren', 'Xander', 'Yuki', 'Zane', 'Aria', 'Bodhi', 'Cleo', 'Dante', 'Esme', 'Felix', 'Greta', 'Hugo', 'Ivy', 'Jonah', 'Kira', 'Leon', 'Mira', 'Nico', 'Otto', 'Pia', 'Rhea', 'Soren', 'Talia', 'Ugo', 'Vera', 'Wyatt'];
const lastNames = ['Adams', 'Brooks', 'Clark', 'Diaz', 'Evans', 'Flores', 'Garza', 'Hale', 'Irwin', 'Jones', 'Kim', 'Lopez', 'Nguyen', 'Ortiz', 'Patel', 'Reyes', 'Singh', 'Tran', 'Underwood', 'Vargas', 'Wong', 'Xu', 'Young', 'Zimmer', 'Bennett', 'Carter', 'Dixon', 'Ellis', 'Foster', 'Grant', 'Hayes', 'Ingram', 'Jensen', 'Khan', 'Lowe', 'Mason', 'Novak', 'Owens', 'Park', 'Quinn', 'Russo', 'Stone', 'Tucker', 'Vance', 'Walsh', 'Yates', 'Zhang', 'Abbott'];

const students: User[] = firstNames.map((first, i) => ({
  id: makeId('student', i + 1),
  email: `${first.toLowerCase()}.${lastNames[i].toLowerCase()}@demo-students.ludolio.com`,
  firstName: first,
  lastName: lastNames[i],
  role: 'student',
  createdAt: iso(160 - i * 2),
}));

const units: Unit[] = [
  { id: makeId('unit', 1), orgId: 'demo-org-0000', code: 'GAME101', name: 'Introduction to Game Design', year: 2026, semester: 'semester_1', isArchived: false, createdAt: iso(90), updatedAt: iso(6), organization: { id: 'demo-org-0000', name: 'Ludolio Demo School' } },
  { id: makeId('unit', 2), orgId: 'demo-org-0000', code: 'GAME210', name: 'Level Design Studio', year: 2026, semester: 'semester_1', isArchived: false, createdAt: iso(72), updatedAt: iso(3), organization: { id: 'demo-org-0000', name: 'Ludolio Demo School' } },
  { id: makeId('unit', 3), orgId: 'demo-org-0000', code: 'GAME240', name: 'Narrative Design for Games', year: 2026, semester: 'semester_2', isArchived: false, createdAt: iso(58), updatedAt: iso(1), organization: { id: 'demo-org-0000', name: 'Ludolio Demo School' } },
];

const unitStudentIds: Record<string, string[]> = {
  [units[0].id]: students.slice(0, 18).map((s) => s.id),
  [units[1].id]: students.slice(2, 24).map((s) => s.id),
  [units[2].id]: [...students.slice(5, 10), ...students.slice(24, 48)].map((s) => s.id),
};

const unitTeacherIds: Record<string, string[]> = {
  [units[0].id]: [teacherPool[0].id, teacherPool[1].id],
  [units[1].id]: [teacherPool[0].id, teacherPool[2].id],
  [units[2].id]: [teacherPool[0].id, teacherPool[3].id],
};

const unitGameIds: Record<string, string[]> = {
  [units[0].id]: [games[0].id, games[1].id, games[2].id],
  [units[1].id]: [games[1].id, games[0].id],
  [units[2].id]: [games[2].id, games[1].id, games[0].id],
};

const pendingEnrollments = [{ id: 'pending-0001', unitId: units[1].id, email: 'morgan.gray@demo-students.ludolio.com', createdAt: iso(1) }];

const achievements: Achievement[] = [
  { id: makeId('achievement-0', 1), gameId: games[0].id, achievementId: `${games[0].id}-loop-starter`, name: 'Loop Starter', description: 'Ship a playable core loop draft in Mechanic Forge.', points: 10, isSecret: false, createdAt: iso(120) },
  { id: makeId('achievement-0', 2), gameId: games[0].id, achievementId: `${games[0].id}-balance-pass`, name: 'Balance Pass', description: 'Complete a full systems balancing iteration with player feedback.', points: 20, isSecret: false, createdAt: iso(118) },
  { id: makeId('achievement-0', 3), gameId: games[0].id, achievementId: `${games[0].id}-meta-breaker`, name: 'Meta Breaker', description: 'Find and resolve an unintended dominant strategy.', points: 40, isSecret: true, createdAt: iso(115) },
  { id: makeId('achievement-0', 4), gameId: games[0].id, achievementId: `${games[0].id}-difficulty-curve`, name: 'Difficulty Curve', description: 'Tune a smooth difficulty ramp validated by playtest data.', points: 20, isSecret: false, createdAt: iso(112) },
  { id: makeId('achievement-0', 5), gameId: games[0].id, achievementId: `${games[0].id}-economy-architect`, name: 'Economy Architect', description: 'Design a balanced in-game economy with healthy sinks and sources.', points: 30, isSecret: false, createdAt: iso(110) },
  { id: makeId('achievement-0', 6), gameId: games[0].id, achievementId: `${games[0].id}-prototype-pro`, name: 'Prototype Pro', description: 'Iterate three playable builds in a single design sprint.', points: 50, isSecret: true, createdAt: iso(108) },
  { id: makeId('achievement-1', 1), gameId: games[1].id, achievementId: `${games[1].id}-greybox-ready`, name: 'Greybox Ready', description: 'Complete a navigable level greybox in Level Lab.', points: 10, isSecret: false, createdAt: iso(120) },
  { id: makeId('achievement-1', 2), gameId: games[1].id, achievementId: `${games[1].id}-flow-tuner`, name: 'Flow Tuner', description: 'Improve encounter pacing and readability based on playtest data.', points: 20, isSecret: false, createdAt: iso(118) },
  { id: makeId('achievement-1', 3), gameId: games[1].id, achievementId: `${games[1].id}-hidden-route`, name: 'Hidden Route', description: 'Design a successful optional path with meaningful reward.', points: 40, isSecret: true, createdAt: iso(115) },
  { id: makeId('achievement-1', 4), gameId: games[1].id, achievementId: `${games[1].id}-blockout-master`, name: 'Blockout Master', description: 'Ship a full level blockout with clear landmarks and sightlines.', points: 20, isSecret: false, createdAt: iso(112) },
  { id: makeId('achievement-1', 5), gameId: games[1].id, achievementId: `${games[1].id}-encounter-designer`, name: 'Encounter Designer', description: 'Author a multi-stage encounter with strong pacing beats.', points: 30, isSecret: false, createdAt: iso(110) },
  { id: makeId('achievement-1', 6), gameId: games[1].id, achievementId: `${games[1].id}-spatial-storyteller`, name: 'Spatial Storyteller', description: 'Communicate narrative through environment design alone.', points: 50, isSecret: true, createdAt: iso(108) },
  { id: makeId('achievement-2', 1), gameId: games[2].id, achievementId: `${games[2].id}-story-hook`, name: 'Story Hook', description: 'Create a compelling opening narrative beat in Narrative Nexus.', points: 10, isSecret: false, createdAt: iso(120) },
  { id: makeId('achievement-2', 2), gameId: games[2].id, achievementId: `${games[2].id}-branch-architect`, name: 'Branch Architect', description: 'Build a branching quest line with meaningful player choice.', points: 20, isSecret: false, createdAt: iso(118) },
  { id: makeId('achievement-2', 3), gameId: games[2].id, achievementId: `${games[2].id}-secret-ending`, name: 'Secret Ending', description: 'Design a hidden ending that still feels earned by the player.', points: 40, isSecret: true, createdAt: iso(115) },
  { id: makeId('achievement-2', 4), gameId: games[2].id, achievementId: `${games[2].id}-dialogue-smith`, name: 'Dialogue Smith', description: 'Write a branching conversation with distinct character voices.', points: 20, isSecret: false, createdAt: iso(112) },
  { id: makeId('achievement-2', 5), gameId: games[2].id, achievementId: `${games[2].id}-world-builder`, name: 'World Builder', description: 'Establish a consistent setting with believable internal lore.', points: 30, isSecret: false, createdAt: iso(110) },
  { id: makeId('achievement-2', 6), gameId: games[2].id, achievementId: `${games[2].id}-agency-advocate`, name: 'Agency Advocate', description: 'Deliver a choice whose consequences reshape a later scene.', points: 50, isSecret: true, createdAt: iso(108) },
];

const achievementUnlockCount = (si: number, gi: number) =>
  Math.round(Math.abs(Math.sin((si + 1) * 1.9 + (gi + 1) * 0.7)) * 6);

const userAchievements: UserAchievement[] = students.flatMap((student, si) => {
  const studentUnit = units.find((unit) => (unitStudentIds[unit.id] || []).includes(student.id)) ?? units[0];
  return games.slice(0, 3).flatMap((game, gi) => achievements.filter((a) => a.gameId === game.id).slice(0, achievementUnlockCount(si, gi)).map((achievement, ai) => ({
    id: makeId(`ua-${si}-${gi}`, ai + 1),
    userId: student.id,
    gameId: game.id,
    gameTitle: game.title,
    achievementId: achievement.achievementId,
    unlockedAt: iso(1 + si + gi + ai),
    unitId: studentUnit.id,
    achievement,
    unit: studentUnit,
  })));
});

const assignments: Assignment[] = units.flatMap((unit, unitIndex) => {
  const primaryGame = games.find((game) => game.id === unitGameIds[unit.id][0])!;
  return (unitStudentIds[unit.id] || []).map((studentId, studentIndex) => {
    const student = students.find((item) => item.id === studentId)!;
    return {
      id: makeId(`assignment-${unitIndex}`, studentIndex + 1),
      studentId: student.id,
      gameId: primaryGame.id,
      assignedBy: unitTeacherIds[unit.id][0],
      startDate: iso(28 - studentIndex),
      endDate: iso(-10 - studentIndex),
      isActive: true,
      createdAt: iso(28 - studentIndex),
      updatedAt: iso(3 + (studentIndex % 4)),
      game: primaryGame,
      student,
    };
  });
});

const algebraAchievements = achievements.filter((achievement) => achievement.gameId === games[0].id);
const photonAchievements = achievements.filter((achievement) => achievement.gameId === games[1].id);
const byteAchievements = achievements.filter((achievement) => achievement.gameId === games[2].id);

const activities = [
  {
    id: 'activity-aq-001',
    unitId: units[0].id,
    gameId: games[0].id,
    achievementId: algebraAchievements[0].id,
    title: 'Core Loop Pitch Checkpoint',
    description: 'A short quiz that checks whether students can identify the player goal, repeatable action, and reward cadence in Mechanic Forge.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'Which option best describes a core loop in a game design prototype?', options: ['A repeatable player action cycle with feedback and reward', 'A one-time cutscene only', 'A bug in the UI'], correctAnswers: ['A repeatable player action cycle with feedback and reward'] },
        { question: 'In Mechanic Forge, what is the best reason to tune reward timing?', options: ['To strengthen player motivation and pacing', 'To make every challenge identical', 'To remove all feedback'], correctAnswers: ['To strengthen player motivation and pacing'] },
        { question: 'Which playtest result most strongly suggests the loop is unclear?', options: ['Players cannot explain why they are repeating the action', 'Players finish the tutorial', 'Players change settings'], correctAnswers: ['Players cannot explain why they are repeating the action'] },
      ],
    },
    createdAt: iso(9),
    updatedAt: iso(2),
  },
  {
    id: 'activity-aq-002',
    unitId: units[0].id,
    gameId: games[0].id,
    achievementId: algebraAchievements[1].id,
    title: 'Reward Economy Reflection',
    description: 'Students explain how they adjusted reward cadence, challenge intensity, and progression clarity during a Mechanic Forge iteration.',
    type: 'short_answer' as const,
    config: {
      questions: [
        { question: 'Describe one mechanic or reward you adjusted to make the loop feel more satisfying to a player.' },
        { question: 'What playtest signal told you the current balance was too easy, too punishing, or too repetitive?' },
      ],
    },
    createdAt: iso(8),
    updatedAt: iso(2),
  },
  {
    id: 'activity-aq-003',
    unitId: units[0].id,
    gameId: games[0].id,
    achievementId: algebraAchievements[2].id,
    title: 'Balance Patch Review',
    description: 'A design review checkpoint where students identify which balancing decision would most likely damage retention or strategic variety.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'Which balancing change is most likely to create a dominant strategy?', options: ['One build clearly outperforms every other option', 'Multiple playstyles remain viable', 'Rewards arrive after player mastery'], correctAnswers: ['One build clearly outperforms every other option'] },
        { question: 'What is the best reason to review retention after a balance patch?', options: ['To see whether the updated loop still feels rewarding', 'To remove player choice', 'To make every level shorter regardless of context'], correctAnswers: ['To see whether the updated loop still feels rewarding'] },
      ],
    },
    createdAt: iso(6),
    updatedAt: iso(1),
  },
  {
    id: 'activity-pl-001',
    unitId: units[1].id,
    gameId: games[1].id,
    achievementId: photonAchievements[0].id,
    title: 'Player Pathing Sprint',
    description: 'A quick design quiz where students predict how players will move through a Level Lab greybox before running a playtest.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'Which layout change most directly improves player readability in a greybox level?', options: ['Clearer sightlines and landmark placement', 'Randomizing every corridor', 'Hiding the objective marker without clues'], correctAnswers: ['Clearer sightlines and landmark placement'] },
        { question: 'What is a strong sign that encounter pacing is off?', options: ['Players face high pressure with no recovery beats', 'Players understand the route quickly', 'Players read the objective text'], correctAnswers: ['Players face high pressure with no recovery beats'] },
        { question: 'Why do designers test pathing before adding final art?', options: ['To verify navigation and flow first', 'To remove all iteration time', 'To lock the level permanently'], correctAnswers: ['To verify navigation and flow first'] },
      ],
    },
    createdAt: iso(9),
    updatedAt: iso(3),
  },
  {
    id: 'activity-pl-002',
    unitId: units[1].id,
    gameId: games[1].id,
    achievementId: photonAchievements[1].id,
    title: 'Encounter Flow Critique',
    description: 'Students document how they adjusted chokepoints, combat beats, and navigation cues during a Level Lab encounter review.',
    type: 'short_answer' as const,
    config: {
      questions: [
        { question: 'Explain one change you made to improve the rhythm between tension, release, and exploration in your level.' },
        { question: 'What player behavior from the playtest told you a room transition or encounter beat needed redesign?' },
      ],
    },
    createdAt: iso(7),
    updatedAt: iso(2),
  },
  {
    id: 'activity-pl-003',
    unitId: units[1].id,
    gameId: games[1].id,
    achievementId: photonAchievements[2].id,
    title: 'Tutorial Space Playtest Review',
    description: 'A checkpoint on whether the opening space teaches the intended interaction language without overexplaining.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'What is the clearest sign that a tutorial space is working?', options: ['Players use the intended mechanic without extra explanation', 'Players skip every prompt and get lost', 'Players cannot find the exit'], correctAnswers: ['Players use the intended mechanic without extra explanation'] },
        { question: 'Which tutorial mistake most often hurts onboarding?', options: ['Teaching too many mechanics at once', 'Giving one safe interaction first', 'Using landmarks to guide movement'], correctAnswers: ['Teaching too many mechanics at once'] },
      ],
    },
    createdAt: iso(5),
    updatedAt: iso(1),
  },
  {
    id: 'activity-bb-001',
    unitId: units[2].id,
    gameId: games[2].id,
    achievementId: byteAchievements[0].id,
    title: 'Branching Dialogue Warm-up',
    description: 'A narrative design quiz on player choice, branching structure, and information gating before building in Narrative Nexus.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'What makes a branching dialogue choice meaningful?', options: ['It changes information, relationship, or outcome', 'It only changes text color', 'It always leads to the same response with no signal'], correctAnswers: ['It changes information, relationship, or outcome'] },
        { question: 'Why do narrative designers gate certain information?', options: ['To control pacing and reveal context at the right moment', 'To remove all player agency', 'To avoid writing consequences'], correctAnswers: ['To control pacing and reveal context at the right moment'] },
        { question: 'Which structure best supports replayable narrative choice?', options: ['Visible branches with distinct consequences', 'One path repeated with different font sizes', 'A random answer generator'], correctAnswers: ['Visible branches with distinct consequences'] },
      ],
    },
    createdAt: iso(8),
    updatedAt: iso(2),
  },
  {
    id: 'activity-bb-002',
    unitId: units[2].id,
    gameId: games[2].id,
    achievementId: byteAchievements[1].id,
    title: 'Quest Motivation Journal',
    description: 'Students reflect on how they framed player motivation, stakes, and quest progression inside Narrative Nexus.',
    type: 'short_answer' as const,
    config: {
      questions: [
        { question: 'Describe one quest objective you wrote and explain why a player would care about completing it.' },
        { question: 'What change would make the narrative stakes clearer earlier in the experience?' },
      ],
    },
    createdAt: iso(6),
    updatedAt: iso(2),
  },
  {
    id: 'activity-bb-003',
    unitId: units[2].id,
    gameId: games[2].id,
    achievementId: byteAchievements[2].id,
    title: 'Choice Consequence Review',
    description: 'Students review a narrative branch and identify whether the consequence signal is strong enough for the player to understand the result of their choice.',
    type: 'quiz' as const,
    config: {
      questions: [
        { question: 'What is the best sign that a narrative consequence is landing?', options: ['The player can connect the earlier choice to the later outcome', 'The consequence is completely hidden forever', 'Every choice has identical results'], correctAnswers: ['The player can connect the earlier choice to the later outcome'] },
        { question: 'Which narrative design issue most weakens player agency?', options: ['Choices that appear different but always collapse into the same result', 'A branch with clear trade-offs', 'A quest with foreshadowed payoff'], correctAnswers: ['Choices that appear different but always collapse into the same result'] },
      ],
    },
    createdAt: iso(4),
    updatedAt: iso(1),
  },
];

const activityLibraryItems = activities.map((activity, index) => ({
  id: `library-${activity.id}`,
  orgId: 'demo-org-0000',
  gameId: activity.gameId,
  achievementId: activity.achievementId,
  createdBy: unitTeacherIds[activity.unitId]?.[index % (unitTeacherIds[activity.unitId]?.length || 1)] || demoTeacher.id,
  visibility: index % 3 === 0 ? 'private' as const : 'org' as const,
  title: activity.title,
  description: activity.description,
  type: activity.type,
  config: activity.config,
  isArchived: false,
  createdAt: activity.createdAt,
  updatedAt: activity.updatedAt,
}));

const responseEntry = (id: string, activityId: string, studentId: string, answer: string, daysAgo: number) => ({
  id,
  activityId,
  studentId,
  response: { answer },
  submittedAt: iso(daysAgo),
});

const activityResponses = [
  responseEntry('response-aq-001', 'activity-aq-001', students[0].id, 'The best answer was the repeatable action cycle with feedback and reward, because that is what made the prototype feel like a real loop.', 1),
  responseEntry('response-aq-002', 'activity-aq-001', students[2].id, 'If players cannot explain why they keep doing the action, the loop probably is not communicating the reward clearly enough.', 2),
  responseEntry('response-aq-003', 'activity-aq-002', students[1].id, 'I reduced the time between actions and rewards so the mechanic felt more satisfying during repeated playtests.', 1),
  responseEntry('response-aq-004', 'activity-aq-002', students[4].id, 'Players stopped experimenting after one obvious strategy worked, so I knew the balance needed more variety.', 2),
  responseEntry('response-aq-005', 'activity-aq-003', students[3].id, 'If one build is clearly strongest, everyone will converge on it and the system loses strategic variety.', 1),
  responseEntry('response-aq-006', 'activity-aq-003', students[5].id, 'Retention matters after a patch because the loop still has to feel rewarding, not just mathematically balanced.', 2),
  responseEntry('response-pl-001', 'activity-pl-001', students[2].id, 'Clearer sightlines and landmarks would make the route easier to read before we even added final art.', 1),
  responseEntry('response-pl-002', 'activity-pl-001', students[4].id, 'Testing pathing early helps confirm the flow works before we spend time polishing the environment.', 2),
  responseEntry('response-pl-003', 'activity-pl-002', students[3].id, 'I added a calmer transition space after the intense encounter so players had time to reset before the next decision point.', 1),
  responseEntry('response-pl-004', 'activity-pl-002', students[6].id, 'The playtest showed players hesitating at the doorway, so I changed the sightline and objective framing.', 2),
  responseEntry('response-pl-005', 'activity-pl-003', students[5].id, 'A tutorial is working when players use the intended mechanic without needing extra verbal explanation.', 1),
  responseEntry('response-pl-006', 'activity-pl-003', students[7].id, 'Trying to teach too many mechanics at once made the opening feel noisy and hard to follow.', 3),
  responseEntry('response-bb-001', 'activity-bb-001', students[5].id, 'A meaningful branch changes what the player knows or what happens next, not just the wording of the line.', 1),
  responseEntry('response-bb-002', 'activity-bb-001', students[7].id, 'Information gating helps pacing because the player gets context at the right moment instead of all at once.', 2),
  responseEntry('response-bb-003', 'activity-bb-002', students[6].id, 'My quest objective worked better once the player understood what relationship or outcome was at stake.', 1),
  responseEntry('response-bb-004', 'activity-bb-002', students[8].id, 'I would surface the stakes earlier so the player understands why the branch matters before choosing.', 2),
  responseEntry('response-bb-005', 'activity-bb-003', students[7].id, 'A consequence lands when the player can clearly connect the earlier choice to the later result.', 1),
  responseEntry('response-bb-006', 'activity-bb-003', students[9].id, 'Fake choices that all collapse to the same outcome weaken agency even if the dialogue looks different.', 2),
];

export interface DemoState {
  currentUser: User;
  teachers: User[];
  students: User[];
  games: Game[];
  units: Unit[];
  unitStudentIds: Record<string, string[]>;
  unitTeacherIds: Record<string, string[]>;
  unitGameIds: Record<string, string[]>;
  pendingEnrollments: Array<{ id: string; unitId: string; email: string; createdAt: string }>;
  pendingTeacherInvites: Array<{ id: string; unitId: string; email: string; createdAt: string }>;
  achievements: Achievement[];
  userAchievements: UserAchievement[];
  assignments: Assignment[];
  activityLibraryItems: Array<{ id: string; orgId: string; gameId: string; achievementId: string; createdBy: string; visibility: 'private' | 'org' | 'global'; title: string; description?: string; type: 'quiz' | 'short_answer'; config?: { questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }> }; isArchived: boolean; createdAt: string; updatedAt: string }>;
  activities: Array<{ id: string; unitId: string; gameId: string; achievementId: string; createdBy?: string; sourceLibraryItemId?: string; title: string; description?: string; type: 'quiz' | 'short_answer'; config?: { questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }> }; isActive?: boolean; createdAt: string; updatedAt: string }>;
  activityResponses: Array<{ id: string; activityId: string; studentId: string; response: { answer: string }; submittedAt: string }>;
}

export function createDemoState(): DemoState {
  return JSON.parse(JSON.stringify({ currentUser: demoTeacher, teachers: teacherPool, students, games, units, unitStudentIds, unitTeacherIds, unitGameIds, pendingEnrollments, pendingTeacherInvites: [], achievements, userAchievements, assignments, activityLibraryItems, activities, activityResponses }));
}